import {
  type FlowAuditQuery,
  type FlowFindAllQueryParams,
  FlowVersionTarget,
  type FlowWithOptionalRunStats,
} from 'flow-schema';

import type { Common } from '../common.js';
import { APIError } from '../models/common.js';
import type { Audit } from '../models/flow/audit.js';
import {
  type FlowWithCreatedBy,
  type FlowWithDefinitions,
  type UpdateValues,
} from '../models/flow/flow.js';
import { type SubflowDefinitionWithFlow } from '../models/flow/flowDefinition.js';

export class API {
  common: Common;
  baseEndpoint = '/flow/flows';

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(queryParams?: FlowFindAllQueryParams): Promise<FlowWithOptionalRunStats[]> {
    const url = this.common.constructPath(this.baseEndpoint, queryParams);
    return await this.common.getJSON(url);
  }

  async findOneById(flowId: string): Promise<FlowWithDefinitions> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${flowId}`);
    return await this.common.getJSON(url);
  }

  async update(flowId: string, values: UpdateValues): Promise<FlowWithCreatedBy> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${flowId}`);
    return await this.common.patchJSON(url, values);
  }

  async getOmniInsightsUrl(args: { flowId: string; timeZone: string }): Promise<{ src: string }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${args.flowId}/omni-insights`);
    return await this.common.postJSON(url, { timeZone: args.timeZone });
  }

  async findAuditTrail(flowId: string, query?: FlowAuditQuery): Promise<Audit[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${flowId}/audit-trail`, query);
    return await this.common.getJSON(url);
  }

  async findSubflowVersion(args: {
    flowId: string;
    target?: FlowVersionTarget;
    flowDefinitionId?: string;
  }): Promise<SubflowDefinitionWithFlow> {
    const { flowId, ...rest } = args;
    const url = this.common.constructPath(`${this.baseEndpoint}/${flowId}/subflow-version`, rest);
    return await this.common.getJSON(url);
  }

  async findSubflowVersionOrNullForPublished404(args: {
    flowId: string;
    target?: FlowVersionTarget;
    flowDefinitionId?: string;
  }): Promise<SubflowDefinitionWithFlow | null> {
    const { flowId, ...rest } = args;
    const url = this.common.constructPath(`${this.baseEndpoint}/${flowId}/subflow-version`, rest);
    try {
      return await this.common.getJSON(url);
    } catch (error) {
      if (
        error instanceof APIError &&
        error.status === 404 &&
        args.target === FlowVersionTarget.Published
      ) {
        // Selected flow does not yet have a published version
        return null;
      }
      throw error;
    }
  }
}
